import { useEffect, useState, useCallback, useRef } from "react";
import MembersSection from "./MembersSection";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { getMembersAll } from "../../services/memberServices";
import { getAllDesignations } from "../../services/designationServices";

const MembersMain = () => {
  const [members, setMembers] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchParams, setSearchParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchType, setSearchType] = useState("");

  const currentRequestRef = useRef(null);
  const membersSectionRef = useRef(null);

  const currentOfficeBearers = [
    { id: "6896dec7da48601d05b73ab0", title: "President", order: 1 },
    {
      id: "6896ded8da48601d05b73ab3",
      title: "Vice President (Membership)",
      order: 2,
    },
    {
      id: "6896dee7da48601d05b73ab6",
      title: "Vice President (Events)",
      order: 3,
    },
    {
      id: "6896def5da48601d05b73ab9",
      title: "Vice President (Education)",
      order: 4,
    },
    { id: "6896df04da48601d05b73abc", title: "General Secretary", order: 5 },
    { id: "6896df10da48601d05b73abf", title: "Joint Secretary", order: 6 },
    { id: "6896df21da48601d05b73ac2", title: "Treasurer", order: 7 },
    { id: "6896df2bda48601d05b73ac5", title: "Joint Treasurer", order: 8 },
  ];

  const currentOfficeBearerIds = currentOfficeBearers.map(
    (bearer) => bearer.id
  );

  const sortMembersByOfficeOrder = useCallback(
    (membersArray) => {
      if (!searchParams.currentOfficeBearers) {
        return membersArray;
      }

      const officeBearerMap = new Map(
        currentOfficeBearers.map((bearer) => [bearer.id, bearer.order])
      );

      return membersArray.sort((a, b) => {
        const orderA = officeBearerMap.get(a.memberDesignation._id) || 999;
        const orderB = officeBearerMap.get(b.memberDesignation._id) || 999;

        if (orderA !== orderB) {
          return orderA - orderB;
        }

        const nameA =
          `${a.profileInfo.firstName} ${a.profileInfo.lastName}`.toLowerCase();
        const nameB =
          `${b.profileInfo.firstName} ${b.profileInfo.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    },
    [searchParams.currentOfficeBearers]
  );

  const fetchMembers = useCallback(
    async (customPage = 1, filters = {}, append = false) => {
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }

      const abortController = new AbortController();
      currentRequestRef.current = abortController;

      try {
        setLoading(true);
        const apiParams = {
          page: customPage,
          limit: 20,
          ...filters,
        };

        const data = await getMembersAll(apiParams, {
          signal: abortController.signal,
        });

        if (!abortController.signal.aborted) {
          let membersData = data.data.data;

          if (filters.currentOfficeBearers) {
            membersData = sortMembersByOfficeOrder(membersData);
          }

          if (append) {
            setMembers((prev) => {
              const combined = [...prev, ...membersData];
              return filters.currentOfficeBearers
                ? sortMembersByOfficeOrder(combined)
                : combined;
            });
          } else {
            setMembers(membersData);
          }

          setHasMore(data.data.pagination.hasNextPage);
          setPage(data.data.pagination.page);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error(err.message);
          setHasMore(false);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
        if (currentRequestRef.current === abortController) {
          currentRequestRef.current = null;
        }
      }
    },
    [sortMembersByOfficeOrder]
  );

  const fetchDesignations = useCallback(async () => {
    try {
      const data = await getAllDesignations();
      const allowedNames = ["EC Member", "Founder Member", "Member"];

      const filtered = data.data.data.filter((designation) =>
        allowedNames.includes(designation.name)
      );

      setDesignations(filtered);
    } catch (err) {
      console.error(err.message);
    }
  }, []);

  const handleNext = useCallback(() => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchMembers(nextPage, searchParams, false);

      requestAnimationFrame(() => {
        const top = membersSectionRef.current
          ? membersSectionRef.current.offsetTop - 100
          : window.innerHeight * 0.4;
        window.scrollTo({ top, behavior: "smooth" });
      });
    }
  }, [hasMore, loading, page, searchParams, fetchMembers]);

  const handlePrev = useCallback(() => {
    if (page > 1 && !loading) {
      const prevPage = page - 1;
      setPage(prevPage);
      fetchMembers(prevPage, searchParams, false);

      requestAnimationFrame(() => {
        const top = membersSectionRef.current
          ? membersSectionRef.current.offsetTop - 100
          : window.innerHeight * 0.4;
        window.scrollTo({ top, behavior: "smooth" });
      });
    }
  }, [page, loading, searchParams, fetchMembers]);

  const handleSearchParamsChange = useCallback(
    (newParams) => {
      const cleanParams = Object.fromEntries(
        Object.entries(newParams).filter(
          ([_, value]) => value !== "" && value !== null && value !== undefined
        )
      );

      const currentParamsStr = JSON.stringify(searchParams);
      const newParamsStr = JSON.stringify(cleanParams);

      if (currentParamsStr !== newParamsStr) {
        setSearchParams(cleanParams);
        setPage(1);
      }
    },
    [searchParams]
  );

  const handleSetPage = useCallback(
    (newPage) => {
      if (newPage !== page && !loading && newPage > 0) {
        setPage(newPage);
        fetchMembers(newPage, searchParams, false);
      }
    },
    [page, loading, searchParams, fetchMembers]
  );

  const handleSetSearchParams = useCallback(
    (newParams) => {
      let finalParams = { ...newParams };

      if (Object.keys(finalParams).length === 0) {
        finalParams = {
          currentOfficeBearers: currentOfficeBearerIds.join(","),
        };
      }

      const currentParamsStr = JSON.stringify(searchParams);
      const newParamsStr = JSON.stringify(finalParams);

      if (currentParamsStr !== newParamsStr) {
        setSearchParams(finalParams);
        setPage(1);
      }
    },
    [searchParams, currentOfficeBearerIds]
  );

  // Updated initial load logic
  useEffect(() => {
    if (isInitialLoad) {
      fetchDesignations();

      // Check if there are any URL parameters first
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlParams = urlParams.toString().length > 0;

      // Only set default to Current Office Bearers if no URL params exist
      if (!hasUrlParams) {
        const defaultParams = {
          currentOfficeBearers: currentOfficeBearerIds.join(","),
        };
        setSearchParams(defaultParams);
        fetchMembers(1, defaultParams, false);
      }
      // If URL params exist, let MembersSection handle them

      setIsInitialLoad(false);
    }
  }, [isInitialLoad, fetchMembers, fetchDesignations, currentOfficeBearerIds]);

  useEffect(() => {
    if (!isInitialLoad && Object.keys(searchParams).length > 0) {
      fetchMembers(page, searchParams, false);
    }
  }, [page, searchParams, fetchMembers, isInitialLoad]);

  useEffect(() => {
    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
    };
  }, []);

  return (
    <main>
      <Header />
      {loading && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-white border-solid"></div>
        </div>
      )}
      <MembersSection
        members={members}
        designations={designations}
        page={page}
        hasMore={hasMore}
        onNext={handleNext}
        onPrev={handlePrev}
        searchParams={searchParams}
        onSearchParamsChange={handleSearchParamsChange}
        loading={loading}
        currentOfficeBearerIds={currentOfficeBearerIds}
        currentOfficeBearers={currentOfficeBearers}
        setPage={handleSetPage}
        setSearchParams={handleSetSearchParams}
        searchType={searchType}
        setSearchType={setSearchType}
        membersSectionRef={membersSectionRef}
      />
      <Footer />
    </main>
  );
};

export default MembersMain;
